import {
  BookOpen,
  CalendarDays,
  Boxes,
  Cpu,
  FlaskConical,
  Briefcase,
  Database,
  Flag,
  FolderOpen,
  Heart,
  History,
  Layers,
  Library,
  LayoutDashboard,
  List,
  MessageSquare,
  MessageSquareHeart,
  MessagesSquare,
  Network,
  Newspaper,
  Palette,
  Shield,
  Smartphone,
  Sparkles,
  SlidersHorizontal,
  Sparkles as SparklesIcon,
  Tag,
  Trash2,
  UserPlus,
  Users,
  LayoutList,
} from "lucide-react";

import { TRASH_FEATURES } from "../../pages/trash/trashTypes";

// The nav, grouped. `feature` gates an entry against the signed-in admin's
// permissions — one feature, or a list of which any one will do; an entry
// without one is always visible.
//
// `countKey` names a field on `GET /api/admin/dashboard`, which returns
// exactly six: booksCount, userCount, categoryCount, recommendersCount,
// favoriteAuthorsCount, collectionsCount. Rows outside that set simply show no
// number rather than a zero the backend never sent.
//
// The design also lists Analytics and Subpillars. Neither has a route in
// App.js, so neither is here — a nav row that goes nowhere is worse than a
// missing one. Add the route first, then the entry.
export const NAV_SECTIONS = [
  {
    id: "dashboard",
    name: "Dashboard",
    icon: LayoutDashboard,
    items: [
      { path: "/admin", name: "Overview", end: true },
      // Overview carries no icon (it renders a spacer), so this group mixes
      // the two. That is the existing behaviour for a row with an icon in a
      // group without one — the spacer keeps both on the same left edge.
      {
        path: "/activity",
        icon: History,
        name: "Activity",
        feature: "admins",
      },
    ],
  },
  {
    id: "data",
    name: "Data",
    icon: Database,
    items: [
      {
        path: "/favorite-authors",
        icon: UserPlus,
        name: "Authors",
        feature: "authors",
        countKey: "favoriteAuthorsCount",
      },
      {
        path: "/books",
        icon: BookOpen,
        name: "Books",
        feature: "books",
        countKey: "booksCount",
      },
      {
        path: "/series",
        icon: Library,
        name: "Series",
        feature: "series",
      },
      { path: "/summary", icon: List, name: "Summaries", feature: "summaries" },
      {
        path: "/recommend",
        icon: Heart,
        name: "Recommenders",
        feature: "recommenders",
        end: true,
        countKey: "recommendersCount",
      },
      { path: "/home", icon: Newspaper, name: "Posts", feature: "posts" },
      {
        path: "/collections",
        icon: Layers,
        // Labelled Journeys in the UI; the route, permission key and API all
        // still say collections.
        name: "Journeys",
        feature: "collections",
        countKey: "collectionsCount",
      },
    ],
  },
  {
    id: "screens",
    name: "App Screens",
    icon: Smartphone,
    items: [
      { path: "/picks", icon: Sparkles, name: "Picks", feature: "picks" },
      // The Summary tab, as one row. The featured card is a SECTION of that
      // tab, so it is reached from the table's edit icon rather than sitting
      // beside it in the nav — two entries implied two screens that have
      // nothing to do with each other.
      {
        path: "/summary-sections",
        icon: LayoutList,
        name: "Summary",
        feature: "summarySections",
      },
    ],
  },
  {
    id: "presets",
    name: "Presets",
    icon: Boxes,
    items: [
      {
        path: "/genre",
        icon: Palette,
        name: "Genres",
        feature: "genres",
        countKey: "categoryCount",
      },
      {
        path: "/professions",
        icon: Briefcase,
        name: "Professions",
        feature: "professions",
      },
      { path: "/tags", icon: Tag, name: "Tags", feature: "tags" },
      { path: "/pillars", icon: Network, name: "Pillars", feature: "pillars" },
    ],
  },
  {
    id: "management",
    name: "Management",
    icon: SlidersHorizontal,
    items: [
      // A group rather than three loose rows: whoever chooses the model, reads
      // what it spends and writes what it is told are the same person, and
      // Management had grown long enough that they were scattered through it.
      //
      // `tabbed`: one row in the sidebar instead of a fold-out. Clicking it opens
      // the first page the admin can see, and the pages switch between each
      // other from a tab bar above the content (see SectionTabs).
      {
        id: "ai",
        icon: SparklesIcon,
        name: "AI",
        tabbed: true,
        children: [
          {
            path: "/ai-settings",
            icon: FlaskConical,
            name: "AI Settings",
            feature: "aiSettings",
          },
          {
            path: "/ai-usage",
            icon: Cpu,
            name: "AI activity",
            feature: "aiSettings",
          },
          // Was "Drive" — the team's shared prompts and reference text, which
          // is what it has always held and now what it is called.
          {
            path: "/drive",
            icon: FolderOpen,
            name: "AI prompts",
            feature: "drive",
          },
        ],
      },
      {
        path: "/users",
        icon: Users,
        name: "Users",
        feature: "users",
        countKey: "userCount",
      },
      // Everything readers leave behind, in one place. Each row keeps the
      // permission it had — Reports still goes with posts, Feedback with
      // summaries — so grouping them changes what they look like, not who
      // can see them.
      {
        id: "interactions",
        icon: MessagesSquare,
        name: "User Interactions",
        tabbed: true,
        children: [
          {
            path: "/comments",
            icon: MessageSquare,
            name: "Comments",
            feature: "comments",
          },
          { path: "/reports", icon: Flag, name: "Reports", feature: "posts" },
          {
            path: "/feedback",
            icon: MessageSquareHeart,
            name: "Feedback",
            feature: "summaries",
          },
        ],
      },
    ],
  },
];

// Rows that sit on their own, below the groups — no header, nothing to fold.
export const NAV_SINGLES = [
  // Deleted content from every catalogue page, restorable for 30 days.
  { path: "/trash", icon: Trash2, name: "Trash", feature: TRASH_FEATURES },
  // Panel logins and the leave register, one row with a tab each. Not the
  // same people — an employee need not have a panel login — and each tab keeps
  // its own permission, so an admin with only `employees` sees only that tab.
  {
    id: "team",
    icon: Shield,
    name: "Team",
    tabbed: true,
    children: [
      { path: "/team", icon: Users, name: "Members", feature: "admins" },
      {
        path: "/employees",
        icon: CalendarDays,
        name: "Employees",
        feature: "employees",
      },
    ],
  },
];

/**
 * Every collapsible id, sections and the groups nested inside them, so the
 * sidebar can remember which are folded without knowing the shape by heart.
 * A tabbed group never folds, so it has no fold state.
 */
export const ALL_GROUP_IDS = NAV_SECTIONS.flatMap((section) => [
  section.id,
  ...section.items
    .filter((item) => item.children && !item.tabbed)
    .map((item) => item.id),
]);
